// resolveOnboardingStep.js -- the onboarding gate's single decision (spec §9). Pure: no storage,
// network or clock reads; the hook supplies `now`.
export const STEPS = Object.freeze({ BLOCKED: 'blocked', LOADING: 'loading', ACCOUNT_ERROR: 'account_error', WELCOME: 'welcome', AGE: 'age', TERMS: 'terms', SIGNIN: 'signin', APP: 'app' });
export const ANALYTICS_STEPS = new Set(['signin', 'app']); // not 'terms': nothing is captured before acceptance (2026-10-08)
export const LOCK_MS = 24 * 60 * 60 * 1000;
export const FRESH_MS = 60 * 60 * 1000;

const ms = (s) => Date.parse(s);
const locked = (now, at) => now - Math.min(ms(at), now) < LOCK_MS;
// A phone record counts for a signed-in user only while it is fresh and unattached. Once attached, its
// rows are in the outbox (non-parked entries count) or on the account; a parked or dropped entry must
// not keep the user satisfied (spec §10: the user is asked again).
const freshFor = (rec, now) => Boolean(rec) && !rec.attachedTo && now - ms(rec.attestedAt) <= FRESH_MS;
const queued = (device, user, pred) => device.queue.some((q) => q.userId === user.id && !q.parked && q.rows.some(pred));
function cachedPass({ user, device, versions }) {
  const p = device.accountPass;
  return Boolean(user && p && p.userId === user.id && p.terms === versions.terms && p.privacy === versions.privacy);
}

export function isAgeSatisfied(input) {
  const { user, device, account, now } = input;
  if (!user) return device.age?.result === 'passed';
  if (account.status === 'error') return cachedPass(input);
  // Rule 6 is an OR (#1438 pre-Codex F1): an older (expired) under_21 latest row must not hide a queued
  // or fresh pass. An active under_21 is rule 4's (blocked), decided before this runs.
  if (account.latest?.age?.result === 'passed') return true;
  return freshFor(device.age, now) || queued(device, user, (x) => x.kind === 'age' && x.result === 'passed');
}

function isLegalSatisfied(input) {
  const { user, device, account, versions, now } = input;
  const match = (t, p) => t === versions.terms && p === versions.privacy;
  if (!user) return Boolean(device.legal && match(device.legal.terms, device.legal.privacy));
  if (account.status === 'error') return cachedPass(input);
  const l = account.latest;
  if (l?.terms && l?.privacy && match(l.terms.version, l.privacy.version)) return true;
  if (device.legal && match(device.legal.terms, device.legal.privacy) && freshFor(device.legal, now)) return true;
  return queued(device, user, (x) => x.kind === 'terms' && x.version === versions.terms)
    && queued(device, user, (x) => x.kind === 'privacy' && x.version === versions.privacy);
}

export function resolveOnboardingStep(input) {
  const { now, authResolved, user, showAuth, device, account } = input;
  if (device.lock && locked(now, device.lock.at)) return STEPS.BLOCKED;
  if (!authResolved) return STEPS.LOADING;
  // #1464: an active under_21 the gate ever observed for this account (any of its reads, or an ST021 on its
  // send) blocks it ahead of its account state and cached pass, so no later snapshot, state change or auth
  // reversal can undo it. Keyed by account: never another user or a guest.
  const blocks = device.blocks || {};
  if (user && Object.prototype.hasOwnProperty.call(blocks, user.id) && locked(now, blocks[user.id])) return STEPS.BLOCKED;
  if (user && (account.uid !== user.id || account.status === 'idle' || account.status === 'loading')) return STEPS.LOADING;
  if (user && account.status === 'error' && !cachedPass(input)) return STEPS.ACCOUNT_ERROR;
  // Rule 4 keys on the newest under_21 row, not the latest age row: a raced under_21 can commit after a
  // passed yet carry the earlier created_at (PR 1b pre-Codex F1). Mirrors the server guard.
  const u21 = account.latest?.under21;
  if (user && u21 && locked(now, u21.created_at)) return STEPS.BLOCKED;
  if (!device.welcomeSeen && !user) return STEPS.WELCOME;
  if (!isAgeSatisfied(input)) return STEPS.AGE;
  if (!isLegalSatisfied(input)) return STEPS.TERMS;
  if (!user && showAuth) return STEPS.SIGNIN;
  return STEPS.APP;
}
